<template>
  <dialog
    open
    class="mmm-mobile-navigation-dialog"
    :class="{ 'mmm-mobile-navigation-dialog--open': isOpen }"
    ref="dialog"
    aria-modal="false"
  >
    <!-- Titlebar -->
    <Titlebar
      :root-path="rootPath"
      :open="true"
      @toggle="closeNavigation()"
      class="mmm-mobile-navigation-dialog__titlebar"
    ></Titlebar>
    <!-- actual navigation -->
    <nav
      @transitionend="toggle()"
      ref="navigationRef"
      class="mmm-mobile-navigation-dialog__navigation"
      :class="{ 'mmm-mobile-navigation-dialog__navigation--open': isOpen }"
    >
      <!-- actual navigation tree -->
      <div class="mmm-mobile-navigation" role="menu">
        <NavItemMobile
          :depth="0"
          :index="i"
          v-for="(item, i) in tree"
          :key="item.path + '-' + i"
          :item="item"
          :has-border="true"
          :ref="
            el => {
              if (el && typeof el === 'object' && 'handleKeyLeft' in el) {
                items[i] = el as unknown as INavItemMobile;
              } else {
                items[i] = null;
              }
            }
          "
        />
      </div>
      <mmm-contact-static></mmm-contact-static>
    </nav>

    <!-- backdrop closer -->
    <button
      class="mmm-mobile-navigation-dialog__backdrop-closer"
      @click="closeNavigation()"
      aria-label="Hiermit verlassen Sie die mobile Navigation"
    ></button>
  </dialog>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { type ISubHeaderNavItem, EKeyCode, getFirstFocusableElement } from '../helper';

import NavItemMobile, { type INavItemMobile } from './NavItemMobile.ce.vue';
import Titlebar from './TitlebarMobile.ce.vue';

export interface INavigationMobileComponent {
  openLogin: () => void;
}

defineProps<{
  rootPath: string;
  tree: ISubHeaderNavItem[];
}>();

const emits = defineEmits(['toggle']);

const items = ref<(INavItemMobile | null)[]>([]);

const navigationRef = ref<HTMLElement>();
const dialog = ref<HTMLDialogElement>();
const isLoginMenuOpen = ref(false);
const isOpen = ref(false);

function closeNavigation() {
  isOpen.value = false;
}

function toggle() {
  if (!isOpen.value) {
    emits('toggle');
  }
}

function handleKeyup(e: KeyboardEvent) {
  switch (e.code) {
    case EKeyCode.Escape:
      if (isLoginMenuOpen.value === true) {
        // do not close if dialog is open;
        return;
      }
      return closeNavigation();
  }
}

onMounted(() => {
  setTimeout(() => {
    isOpen.value = true;
    window.addEventListener('keydown', handleKeyup, { passive: true });
    const firstElement = getFirstFocusableElement(dialog.value!);
    if (firstElement) {
      firstElement.focus();
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyup);
});
</script>

<style lang="scss" scoped>
@use '../../../common/styles/utils/utils';

.mmm-mobile-navigation-dialog {
  --nav-max-width: 420px;
  --transform-function: cubic-bezier(0.37, 0, 0.52, 1);
  height: 100%; //TODO: check native mobile if we need svh
  width: 100vw;
  border: none;
  padding: 0;
  margin: 0;
  pointer-events: none;
  background-color: transparent;
  backdrop-filter: blur(0px);
  transition: all cubic-bezier(0.37, 0, 0.52, 1) 0.2s;
  position: fixed;

  &__titlebar {
    position: relative;
    z-index: 99;
  }

  &__backdrop-closer {
    position: absolute;
    left: var(--nav-max-width);
    top: var(--header-mobile-height);
    width: calc(100% - var(--nav-max-width));
    height: 100%;
    z-index: 22;
    background-color: transparent;
    border: none;
    outline: none;
  }

  &--open {
    background-color: rgba(0, 0, 0, 0.54);
    backdrop-filter: blur(5px);
    pointer-events: all;
  }

  &__navigation {
    background-color: var(--color-background-background);
    width: 100%;
    max-width: var(--nav-max-width);
    height: calc(100% - var(--header-mobile-height));
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    position: absolute;
    top: var(--header-mobile-height);
    transition: all var(--transform-function) 0.2s;
    transform: translate(-101%, 0);

    .mmm-mobile-navigation-dialog--open & {
      transform: translate(0, 0);
    }
  }
}

.mmm-mobile-navigation {
  padding: 0;
  margin: 0;
  list-style: none;
}

.mmm-mobile-login-button {
  border-radius: var(--options-button-radius);
  height: var(--options-button-height);
  padding: var(--options-button-padding);
  text-decoration: none;
  margin: utils.px-to-rem(38) utils.px-to-rem(16) utils.px-to-rem(16);
  background-color: transparent;
  color: var(--color-primary);
  font-weight: 700;
  font-size: utils.px-to-rem(18);
  width: calc(100% - 32px);
}

.nav__submenu {
  background-color: var(--overlay-background);
  color: var(--default-text-color);
  padding: 0;
  margin: 0;
  list-style: none;
  height: 0;
  overflow: hidden;
  transition: all ease-in-out 0.2s;
  padding: 0 0;
}
</style>
